/**
 * fieldLayout.ts
 * Post-render layout rules for filled DOCX HTML:
 *  - Identifier codes: nowrap + auto font-shrink (never truncate the value)
 *  - Content fields: wrap with padding; shrink font only when the cell has a fixed height
 *  - Emit warnings when content still overflows after min font size
 */

import { classifyFieldKind, type FieldKind } from "./fieldKind";

export const DEFAULT_MIN_FONT_PX = 7;
export const DEFAULT_BASE_FONT_PX = 10.5;
export const CONTENT_PAD_Y_PX = 4;
/** Footer safe zone from the physical bottom edge (≈5–7 mm). */
export const FOOTER_SAFE_MM = 6;
export const FOOTER_SAFE_PX = Math.round((FOOTER_SAFE_MM / 25.4) * 96); // ≈23px

export interface LayoutWarning {
  kind: "identifier_overflow" | "content_overflow" | "footer_collision";
  message: string;
  fieldHint?: string;
}

export interface FitTextOptions {
  minFontPx?: number;
  baseFontPx?: number;
}

/**
 * Shrink font until the element's scrollWidth fits clientWidth (single-line),
 * or until min font is reached. Never mutates the text content.
 * Returns false when still overflowing at the minimum size.
 */
export function fitTextToWidth(el: HTMLElement, options: FitTextOptions = {}): boolean {
  const min = options.minFontPx ?? DEFAULT_MIN_FONT_PX;
  let size = parseFloat(getComputedStyle(el).fontSize) || options.baseFontPx || DEFAULT_BASE_FONT_PX;

  el.style.whiteSpace = "nowrap";
  el.style.wordBreak = "normal";
  el.style.overflowWrap = "normal";
  el.style.overflow = "visible";

  // Already fits
  if (el.scrollWidth <= el.clientWidth + 1) {
    el.style.fontSize = `${size}px`;
    return true;
  }

  while (size > min && el.scrollWidth > el.clientWidth + 1) {
    size -= 0.5;
    el.style.fontSize = `${size}px`;
  }

  return el.scrollWidth <= el.clientWidth + 1;
}

/**
 * For multi-line content in a fixed-height box: ensure padding, allow wrap,
 * and shrink font until scrollHeight fits (or min font).
 */
export function fitTextToBox(el: HTMLElement, options: FitTextOptions = {}): boolean {
  const min = options.minFontPx ?? DEFAULT_MIN_FONT_PX;
  let size = parseFloat(getComputedStyle(el).fontSize) || options.baseFontPx || DEFAULT_BASE_FONT_PX;

  el.style.whiteSpace = "normal";
  el.style.wordBreak = "break-word";
  el.style.overflowWrap = "break-word";
  el.style.paddingTop = `${CONTENT_PAD_Y_PX}px`;
  el.style.paddingBottom = `${CONTENT_PAD_Y_PX}px`;
  el.style.boxSizing = "border-box";
  el.style.overflow = "visible";
  el.style.lineHeight = "1.35";

  if (!el.clientHeight) {
    el.style.fontSize = `${size}px`;
    return true;
  }

  if (el.scrollHeight <= el.clientHeight + 1) {
    el.style.fontSize = `${size}px`;
    return true;
  }

  while (size > min && el.scrollHeight > el.clientHeight + 1) {
    size -= 0.5;
    el.style.fontSize = `${size}px`;
  }

  return el.scrollHeight <= el.clientHeight + 1;
}

function cellText(el: HTMLElement): string {
  return (el.textContent ?? "").replace(/\s+/g, " ").trim();
}

/**
 * Guess field kind from a table cell's header (same column) or its own text shape.
 */
export function inferCellKind(cell: HTMLElement, headerText: string): FieldKind {
  if (headerText) {
    const fromHeader = classifyFieldKind(headerText);
    if (fromHeader !== "default") return fromHeader;
  }
  const text = cellText(cell);
  // Long digit/code strings without spaces → identifier
  if (/^[A-Za-z0-9\-_.]{6,}$/.test(text) && /\d/.test(text)) return "identifier";
  if (text.length > 28 || /\s/.test(text)) return "content";
  return "default";
}

function headerForColumn(table: HTMLTableElement, cellIndex: number): string {
  const firstRow = table.rows[0];
  if (!firstRow) return "";
  const th = firstRow.cells[cellIndex];
  return th ? cellText(th) : "";
}

/**
 * Apply identifier / content layout rules across every table cell in a rendered
 * DOCX container (preview or off-screen export). Returns warnings for cells that
 * still overflow after font shrink.
 */
export function applyFieldLayoutRules(
  root: HTMLElement,
  options: FitTextOptions = {}
): LayoutWarning[] {
  const warnings: LayoutWarning[] = [];
  const tables = Array.from(root.querySelectorAll("table")) as HTMLTableElement[];

  for (const table of tables) {
    for (let r = 0; r < table.rows.length; r++) {
      const row = table.rows[r];
      for (let c = 0; c < row.cells.length; c++) {
        const cell = row.cells[c] as HTMLElement;
        const text = cellText(cell);
        if (!text) continue;
        // Skip logo-only (or near-empty) cells — fit rules are for text fields.
        if (cell.querySelector("img, svg, canvas") && text.length < 3) continue;

        // Skip pure header row labels (short bold titles often in row 0)
        const header = r === 0 ? text : headerForColumn(table, c);
        const kind = r === 0 ? "default" : inferCellKind(cell, header);

        if (kind === "identifier") {
          cell.style.whiteSpace = "nowrap";
          cell.style.wordBreak = "normal";
          cell.style.overflowWrap = "normal";
          cell.style.overflow = "visible";
          cell.style.lineHeight = "1.35";
          const ok = fitTextToWidth(cell, options);
          if (!ok) {
            warnings.push({
              kind: "identifier_overflow",
              fieldHint: header || text.slice(0, 24),
              message: `Mã định danh "${header || text.slice(0, 24)}" không vừa chiều rộng ô dù đã giảm font tối thiểu — giá trị gốc được giữ nguyên, không cắt ký tự.`,
            });
          }
        } else if (kind === "content") {
          cell.style.whiteSpace = "normal";
          cell.style.wordBreak = "break-word";
          cell.style.overflowWrap = "break-word";
          cell.style.paddingTop = `${CONTENT_PAD_Y_PX}px`;
          cell.style.paddingBottom = `${CONTENT_PAD_Y_PX}px`;
          cell.style.lineHeight = "1.35";
          cell.style.overflow = "visible";
          // Only shrink when the row/cell appears height-constrained
          const fixedHeight =
            !!cell.style.height ||
            !!cell.getAttribute("height") ||
            (row as HTMLElement).style.height !== "" ||
            cell.clientHeight > 0 && cell.scrollHeight > cell.clientHeight + 2;
          if (fixedHeight) {
            const ok = fitTextToBox(cell, options);
            if (!ok) {
              warnings.push({
                kind: "content_overflow",
                fieldHint: header || text.slice(0, 24),
                message: `Nội dung "${header || "ô"}" vượt chiều cao ô sau khi giảm font tối thiểu — kiểm tra lại template.`,
              });
            }
          } else {
            // Grow naturally: clear rigid heights that clip wrapped names
            cell.style.height = "auto";
            (row as HTMLElement).style.height = "auto";
            cell.style.overflow = "visible";
          }
        }
      }
    }
  }

  return warnings;
}

/**
 * Reserve a fixed footer band at the bottom of each page section and place the
 * "Trang X/Y" label inside the paper safe zone (≈6 mm from the bottom edge).
 */
export function ensureFooterZone(
  section: HTMLElement,
  pageLabel: string
): void {
  section.style.position = "relative";
  section.style.boxSizing = "border-box";
  // Keep main content clear of the footer band
  const padBottom = Math.max(
    FOOTER_SAFE_PX + 14,
    parseInt(section.style.paddingBottom || "0", 10) || 0
  );
  section.style.paddingBottom = `${padBottom}px`;

  let footer = section.querySelector(".docx-page-footer") as HTMLElement | null;
  if (!footer) {
    footer = section.ownerDocument!.createElement("div");
    footer.className = "docx-page-footer";
    section.appendChild(footer);
  }
  footer.textContent = pageLabel;
  footer.style.cssText = [
    "position:absolute",
    `bottom:${FOOTER_SAFE_PX}px`,
    "left:0",
    "right:0",
    "text-align:center",
    "font-size:10px",
    "line-height:1.2",
    "color:#444",
    "z-index:5",
    "pointer-events:none",
    "height:14px",
    "overflow:visible",
  ].join(";");
}

/** Build the canonical page label. */
export function pageLabel(page: number, total: number): string {
  return `Trang ${page}/${total}`;
}
